import { useState } from "react";
import * as authSelectors from "../../auth/authSelectors";
import { useSelector } from "react-redux";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { ButtonType } from "./DeleteMenuSelector";
import { ButtonAction, ButtonPrimary } from "../../../components/NewButtons";
import { DropdownItemButton } from "../../common/DropdownItemButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ConfirmActionModal } from "../../../components/ConfirmActionModal";
import {
  useGetMenuSalesChannels,
  usePushMenuToSalesChannels,
} from "../service/menus-api";
import { toastr } from "react-redux-toastr";
import { StyledInfoBox } from "../../../components/StyledInfoBox";
import { SmallBadge } from "../../../components/SmallBadge";
import { colors } from "../../../components/colors";
import { StyledText } from "../../../assets/typography/typography";
import { css } from "@emotion/react";
import { getErrorMessage, useHandleFetchError } from "../../../error-handler";
import { SpinnerBlock } from "../../common/Spinner";

type Props = {
  id?: string;
  onClick?: () => void;
  buttonType?: ButtonType;
};

const badgeAdditionalStyles = css`
  display: inline-block;
  width: fit-content;
  margin-right: 4px;
  margin-bottom: 4px;
`;

export const PushMenuToSalesChannelsSelector = ({
  id,
  onClick,
  buttonType,
}: Props) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const {
    data: salesChannels,
    error,
    isLoading: isLoadingSalesChannels,
  } = useGetMenuSalesChannels(organizationKeyName, id, modalOpen);

  const {
    mutateAsync: pushMenuToSalesChannels,
    isLoading: pushIsLoading,
  } = usePushMenuToSalesChannels(id);

  const canPushMenu =
    (salesChannels || []).filter((res) => !!res.menuPushable).length > 0;

  const handlePushToSalesChannels = async () => {
    if (!id) return;

    try {
      await pushMenuToSalesChannels({
        menuId: id,
        organizationKeyName,
      });
      toastr.success("Success", "Menu successfully pushed");
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      toastr.error("Error", message);
    } finally {
      setModalOpen(false);
    }
  };

  const openModal = () => {
    if (onClick) {
      onClick();
    }
    setModalOpen(true);
  };

  useHandleFetchError(error);

  return (
    <div>
      {buttonType === "regular" ? (
        <ButtonPrimary
          onClick={openModal}
          icon={<FontAwesomeIcon icon={solid("cloud-arrow-up")} />}
          type="button"
        >
          Push this menu externally
        </ButtonPrimary>
      ) : (
        <DropdownItemButton
          onClick={openModal}
          icon={<FontAwesomeIcon icon={solid("cloud-arrow-up")} />}
        >
          Push this menu externally
        </DropdownItemButton>
      )}
      <ConfirmActionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={
          canPushMenu ? "Push menu externally" : "Cannot push menu externally"
        }
        subtitle={
          canPushMenu
            ? "Pushing the menu externally will make the current menu available on all external sales channels using this menu."
            : "This menu is not pushable to any external sales channel."
        }
        confirmButton={
          <ButtonAction
            onClick={handlePushToSalesChannels}
            type="button"
            disabled={pushIsLoading || isLoadingSalesChannels || !canPushMenu}
          >
            {pushIsLoading ? (
              <FontAwesomeIcon icon={solid("spinner")} spin pulse />
            ) : (
              "Push menu externally"
            )}
          </ButtonAction>
        }
      >
        {isLoadingSalesChannels ? (
          <SpinnerBlock />
        ) : (
          <>
            {canPushMenu && (
              <StyledInfoBox
                title="OBS: Changes can take 10-15 min to take effect"
                subtitle="It can take up to 15 min before you can see your changes live in HeapsGo and the following external sales channels:"
              >
                {salesChannels
                  ?.filter((res) => !!res.menuPushable)
                  .map((res, i) => (
                    <SmallBadge
                      key={i}
                      backgroundColor={colors.greyBorder}
                      additionalStyles={badgeAdditionalStyles}
                    >
                      <StyledText className="bodySubtextRegular mb-0">
                        {res.name}
                      </StyledText>
                    </SmallBadge>
                  ))}
              </StyledInfoBox>
            )}
          </>
        )}
      </ConfirmActionModal>
    </div>
  );
};
